export interface MeasurementRowLockEvent {
  action: 'lock' | 'unlock';
  timestamp: string;
}

export interface MeasurementRowLockMetadata {
  _locked: boolean;
  _lastLockedAt?: string;
  _lastUnlockedAt?: string;
  _lockHistory: MeasurementRowLockEvent[];
}

/**
 * Ensures a measurement row always contains the expected lock metadata shape.
 */
export function normalizeMeasurementRowLockMetadata<TRow extends Record<string, any>>(
  row: TRow
): TRow & MeasurementRowLockMetadata {
  return {
    ...row,
    _locked: row._locked === true,
    _lastLockedAt: typeof row._lastLockedAt === 'string' ? row._lastLockedAt : undefined,
    _lastUnlockedAt: typeof row._lastUnlockedAt === 'string' ? row._lastUnlockedAt : undefined,
    _lockHistory: Array.isArray(row._lockHistory) ? row._lockHistory : [],
  };
}

/**
 * Creates a fresh unlocked metadata payload for a copied or newly created row.
 */
export function resetMeasurementRowLockMetadata<TRow extends Record<string, any>>(
  row: TRow
): TRow & MeasurementRowLockMetadata {
  return {
    ...row,
    _locked: false,
    _lastLockedAt: undefined,
    _lastUnlockedAt: undefined,
    _lockHistory: [],
  };
}

/**
 * Applies a lock or unlock action and records it in the row history.
 */
export function setMeasurementRowLocked<TRow extends Record<string, any>>(
  row: TRow,
  locked: boolean,
  timestamp = new Date().toISOString()
): TRow & MeasurementRowLockMetadata {
  const normalizedRow = normalizeMeasurementRowLockMetadata(row);

  if (normalizedRow._locked === locked) {
    return normalizedRow;
  }

  return {
    ...normalizedRow,
    _locked: locked,
    _lastLockedAt: locked ? timestamp : normalizedRow._lastLockedAt,
    _lastUnlockedAt: locked ? normalizedRow._lastUnlockedAt : timestamp,
    _lockHistory: [
      ...normalizedRow._lockHistory,
      {
        action: locked ? 'lock' : 'unlock',
        timestamp,
      },
    ],
  };
}
